import { useCallback, useEffect, useReducer } from "react";
import { useDebounce } from "use-debounce";

import { getContracts } from "../../../../../modules/contracts/api/contract";
import { Contract } from "../../../../../modules/contracts/api/contract.schema";

interface ReducerState {
    isLoading: boolean;
    searchTerm: string;
    contracts: Array<Contract>;
    dropdownOpen: boolean;
}

interface SearchTermAction {
    type: "searchTerm",
    payload: ReducerState["searchTerm"];
}

interface ContractAction {
    type: "contracts",
    payload: ReducerState["contracts"];
}

interface DropdownOpenAction {
    type: "dropdownOpen",
    payload: ReducerState["dropdownOpen"],
}

type ReducerAction = SearchTermAction | ContractAction | DropdownOpenAction;

const initialReducerState = {
    isLoading: false,
    searchTerm: "",
    contracts: [],
    dropdownOpen: false,
}

function reducer(state: ReducerState, action: ReducerAction) {
    switch (action.type) {
        case "searchTerm":
            return {
                ...state,
                isLoading: action.payload ? true : false,
                searchTerm: action.payload,
            }
        case "dropdownOpen":
            return {
                ...state,
                dropdownOpen: action.payload,
            }
        case "contracts":
            return {
                ...state,
                isLoading: false,
                contracts: action.payload,
            }
        default:
            return state;
    }
}

export default function useSearchContracts() {
    const [state, dispatch] = useReducer(reducer, initialReducerState);
    const [debouncedSearchTerm] = useDebounce(state.searchTerm, 500);

    useEffect(() => {
        const updateData = async () => {
            if (!debouncedSearchTerm) {
                dispatch({
                    type: "contracts",
                    payload: [],
                });
            }

            const response = await getContracts({
                search: debouncedSearchTerm,
            });

            dispatch({
                type: "contracts",
                payload: response || [],
            });
        }

        updateData();
    }, [debouncedSearchTerm]);

    const setDropdownOpen = useCallback((isOpen: ReducerState["dropdownOpen"]) => {
        dispatch({
            type: "dropdownOpen",
            payload: isOpen,
        });
    }, []);

    return {
        ...state,
        setDropdownOpen,
        setSearchTerm: (term: string) => {
            dispatch({
                type: "searchTerm",
                payload: term,
            })
        },
    }
}
